import { useCallback } from 'react';
import { Form, FormRenderProps, RenderableProps } from 'react-final-form';
import { Box, Typography } from '@mui/material';

import { ConfirmDialog, HelpTip, LinearProgress } from '@occc-app/design-system';
import { Retailer_Network_User_Role, RetailerNetworkLocationDataFragment } from '@occc/graphql';
import { pluralize } from '@occc-app/lib/strings';

import { AssociateUsersWithLocationsFormValues } from './associate-users-with-locations-dialog.types';
import { validate } from './associate-users-with-locations-dialog.validation';

import { RetailerNetworkUserMultipleSelectField } from '../retailer-network-user-multiple-select-field.component';
import { RetailerNetworkLocationMultipleSelectField } from '../retailer-network-location-multiple-select-field.component';
import { useRetailerNetworkUserSelector } from '../../retailer-network-context/use-retailer-network-user.selector';

type AssociateUsersWithLocationsDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: AssociateUsersWithLocationsFormValues) => void;
  loading: boolean;
  retailerNetworkUuid: string;
  location: RetailerNetworkLocationDataFragment;
};

export const AssociateUsersWithLocationsDialog = ({
  open,
  onClose,
  onSubmit,
  loading,
  retailerNetworkUuid,
  location,
}: AssociateUsersWithLocationsDialogProps) => {
  const { isRetailerNetworkSiteAdmin, retailerNetworkUserOpenCommerceUserUuid } =
    useRetailerNetworkUserSelector();
  const renderForm = useCallback<
    NonNullable<RenderableProps<FormRenderProps<AssociateUsersWithLocationsFormValues>>['render']>
  >(
    ({ handleSubmit, valid, values }) => {
      const countOfUsers = values.users?.length;
      const confirmButtonText = `Add ${countOfUsers || ''} ${pluralize(countOfUsers, 'user')}`;

      return (
        <ConfirmDialog
          dialogTitle='Choose Users to Add'
          onClose={onClose}
          open={open}
          fullWidth
          maxWidth='sm'
          confirmButtonText={confirmButtonText}
          confirmButtonProps={{
            onClick: handleSubmit,
            disabled: !valid || loading,
          }}
        >
          {loading && <LinearProgress />}
          <HelpTip title='Adding Users to Locations Only'>
            <Typography variant='body2' color='textSecondary'>
              To add Users or Admins to the entire Network, add them from the Network Details page.
            </Typography>
          </HelpTip>
          <Box mt={1}>
            <RetailerNetworkUserMultipleSelectField
              name='users'
              label='Select Users'
              retailerNetworkUuidEquals={retailerNetworkUuid}
              roleIn={
                isRetailerNetworkSiteAdmin ? [Retailer_Network_User_Role.SiteUser] : undefined
              }
            />
            <RetailerNetworkLocationMultipleSelectField
              name='locations'
              label='Select Locations'
              retailerNetworkUuidEquals={retailerNetworkUuid}
              openCommerceUserUuidEquals={
                isRetailerNetworkSiteAdmin ? retailerNetworkUserOpenCommerceUserUuid : undefined
              }
            />
          </Box>
        </ConfirmDialog>
      );
    },
    [
      loading,
      onClose,
      open,
      retailerNetworkUuid,
      isRetailerNetworkSiteAdmin,
      retailerNetworkUserOpenCommerceUserUuid,
    ],
  );

  return (
    <Form<AssociateUsersWithLocationsFormValues>
      onSubmit={onSubmit}
      validate={validate}
      render={renderForm}
      initialValues={{ locations: [location] }}
    />
  );
};
